import {
  IQuoteItemWithDisplayType,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  PriceUpliftConfigFormType,
  PriceUpliftConfigTypeEnum,
  PriceUpliftPercentageTypeEnum,
  ProductTypeEnum,
  QuoteOfferingForUplift,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { logger } from '../../services/logger';
import { getLengthStartEndDisplay } from '../../utils/dates';
import { backCalculateUpliftPercentages } from '../../utils/upliftPrice.utils';
import {
  MAccordion,
  MAccordionButton,
  MAccordionIcon,
  MAccordionItem,
  MAccordionPanel,
  MAlert,
  MBox,
  MButton,
  MDivider,
  MFlex,
  MText,
} from '../Monetize';
import { PriceUpliftGrid } from './PriceUpliftGrid';

export type PriceUpliftDuringContractProps = {
  quote: IQuoteRespSchema;
  quoteOffering: QuoteOfferingForUplift;
  childQuoteOfferings: QuoteOfferingForUplift[];
  quotePrices: IQuotePrice[];
  offeringRate: IRateResSchema;
  isLoading: boolean;
  hasAnyUplifts?: boolean;
  onApply?: () => Promise<void>;
  onClearAll?: () => void;
  calculateUpliftForAllItems: (
    upliftPercentage: string,
    quoteOffering: QuoteOfferingForUplift,
  ) => void;
  calculateUpliftForSingleItem: (
    upliftPercentage: string,
    quoteOffering: QuoteOfferingForUplift,
    productId: string,
  ) => void;
  clearProductUpliftsForSegment?: (
    quoteOffering: QuoteOfferingForUplift,
  ) => void;
  setEditMode?: (editMode: boolean) => void;
  syncBackCalculatedUplifts: (
    segmentUplifts: Record<string, number>,
    productUplifts: Record<string, Record<string, number>>,
  ) => void;
};

export const PriceUpliftDuringContract = ({
  quote,
  quoteOffering,
  childQuoteOfferings,
  quotePrices,
  offeringRate,
  isLoading,
  hasAnyUplifts = false,
  onApply,
  onClearAll,
  calculateUpliftForAllItems,
  calculateUpliftForSingleItem,
  clearProductUpliftsForSegment,
  setEditMode,
  syncBackCalculatedUplifts,
}: PriceUpliftDuringContractProps) => {
  const hasSyncedRef = useRef(false);

  // Check if all usage products have their prices loaded
  const hasIncompleteUsagePrices = childQuoteOfferings.some((offering) =>
    offering.items.some(
      (item) =>
        item.productType === ProductTypeEnum.USAGE &&
        !item.unitPriceAfterDiscount,
    ),
  );

  const calculateSegmentUpliftData = (
    segment: QuoteOfferingForUplift,
    baseItems: IQuoteItemWithDisplayType[],
  ) => {
    const { segmentUpliftPercentage, productUplifts, isUniformUplift } =
      backCalculateUpliftPercentages(segment, baseItems);

    const productConfigurations = segment.items.map(({ productId }) => {
      const productUplift = productUplifts.find(
        (p) => p.productId === productId,
      );
      const upliftAmount = productUplift?.upliftPercentage;
      return {
        productId,
        amount: upliftAmount && upliftAmount > 0 ? upliftAmount : null,
        percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      };
    });

    return {
      type:
        isUniformUplift && segment.items.length > 1
          ? PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
          : PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
      amount:
        isUniformUplift &&
        segment.items.length > 1 &&
        segmentUpliftPercentage &&
        segmentUpliftPercentage > 0
          ? segmentUpliftPercentage
          : null,
      percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      productConfigurations,
    };
  };

  const formValues = useMemo(() => {
    if (!hasIncompleteUsagePrices) {
      const segments = childQuoteOfferings.map((segment, index) => {
        const baseItems =
          index === 0
            ? quoteOffering.items
            : childQuoteOfferings[index - 1].items;

        return calculateSegmentUpliftData(segment, baseItems);
      });

      return { segments };
    }

    return { segments: [] };
  }, [childQuoteOfferings, quoteOffering.items, hasIncompleteUsagePrices]);

  const formValuesWithCurrent = useMemo(() => {
    if (!hasIncompleteUsagePrices) {
      return {
        current: {
          type: PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
          amount: null,
          percentageType: PriceUpliftPercentageTypeEnum.FIXED,
          productConfigurations: quoteOffering.items.map(({ productId }) => ({
            productId,
            amount: null,
            percentageType: PriceUpliftPercentageTypeEnum.FIXED,
          })),
        },
        segments: formValues.segments,
      };
    }
  }, [quoteOffering.items, formValues.segments, hasIncompleteUsagePrices]);

  const defaultFormValues = {
    current: {
      type: PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
      amount: null,
      percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      productConfigurations: quoteOffering.items.map(({ productId }) => ({
        productId,
        amount: null,
        percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      })),
    },
    segments: childQuoteOfferings.map(({ items }) => ({
      type: PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
      amount: null,
      percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      productConfigurations: items.map(({ productId }) => ({
        productId,
        amount: null,
        percentageType: PriceUpliftPercentageTypeEnum.FIXED,
      })),
    })),
  };

  const { control, setValue, reset } = useForm<{
    current: PriceUpliftConfigFormType;
    segments: PriceUpliftConfigFormType[];
  }>({
    defaultValues: formValuesWithCurrent,
  });

  const clearAll = () => {
    reset(defaultFormValues);
    onClearAll?.();
  };

  useEffect(() => {
    if (!hasIncompleteUsagePrices) {
      reset(formValuesWithCurrent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, hasIncompleteUsagePrices]);

  useEffect(() => {
    if (
      formValues?.segments.length &&
      formValues?.segments.length > 0 &&
      !hasSyncedRef.current
    ) {
      if (hasIncompleteUsagePrices) {
        return;
      }

      const segmentUplifts: Record<string, number> = {};
      const productUplifts: Record<string, Record<string, number>> = {};

      formValues.segments.forEach((segment, index) => {
        const segmentIndex = index.toString();

        if (
          segment.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS &&
          segment.amount !== null &&
          segment.amount > 0
        ) {
          segmentUplifts[segmentIndex] = segment.amount;
        }

        if (
          segment.productConfigurations?.length > 0 &&
          segment.type === PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC
        ) {
          const productMap: Record<string, number> = {};
          let hasProductUplifts = false;

          segment.productConfigurations.forEach((productConfig) => {
            if (productConfig.amount !== null && productConfig.amount > 0) {
              productMap[productConfig.productId] = productConfig.amount;
              hasProductUplifts = true;
            }
          });

          if (hasProductUplifts) {
            productUplifts[segmentIndex] = productMap;
          }
        }
      });

      if (
        Object.keys(segmentUplifts).length > 0 ||
        Object.keys(productUplifts).length > 0
      ) {
        syncBackCalculatedUplifts(segmentUplifts, productUplifts);
        hasSyncedRef.current = true;
      }
    }
  }, [syncBackCalculatedUplifts, formValues, hasIncompleteUsagePrices]);

  useEffect(() => {
    if (setEditMode) {
      setEditMode(hasAnyUplifts);
    }
  }, [hasAnyUplifts, setEditMode]);

  return (
    <MAccordion
      multiple
      mb={4}
      defaultValue={childQuoteOfferings.length === 0 ? [] : ['0']}
    >
      <MAccordionItem value="0" border="none">
        <MAccordionButton px={0} _hover={{ bg: 'none' }}>
          <MText flex="1" textAlign="left" fontWeight="bold" fontSize="md">
            Price Uplift during Contract
          </MText>
          <MAccordionIcon />
        </MAccordionButton>
        <MBox>
          <MText pt={2}>
            Calculate and apply a price uplift for all products in your
            Offering. The price increase will be based on the previous schedule
            change's Price Per Unit (PPU).
          </MText>
          <MText pt={2}>
            Uplifting the price will create custom rates for each segment and
            the uplift will be applied to all tiers within the rate.
          </MText>
        </MBox>
        <MAccordionPanel m={0} px={0}>
          <MDivider my={4} />
          <MBox>
            <MFlex>
              <MText fontWeight="semibold">Current Dates:</MText>
              <MText ml={1}>
                {toDateShort(quoteOffering.startDate)}
                {' - '}
                {toDateShort(quoteOffering.endDate)}
                {' ('}
                {getLengthStartEndDisplay(
                  quoteOffering.startDate,
                  quoteOffering.endDate,
                )}
                {')'}
              </MText>
            </MFlex>
            <PriceUpliftGrid
              namePrefix="current"
              ppuLabel="Current PPU"
              isLockedOrIsReadOnly={false}
              items={quoteOffering.items}
              control={control as any}
              setValue={setValue as any}
              percentageType={PriceUpliftPercentageTypeEnum.FIXED}
              quote={quote}
              quoteOffering={quoteOffering}
              quotePrices={quotePrices}
              offeringRate={offeringRate ?? null}
              parentQuoteOfferingId={quoteOffering.id}
            />
            <MDivider my={2} />
          </MBox>
          {childQuoteOfferings.length === 0 && (
            <MAlert type="error" w="full" m={0}>
              <MText>
                There are no schedule changes to uplift. Create a schedule
                change to this Offering to add an uplift.
              </MText>
            </MAlert>
          )}
          {childQuoteOfferings.map((segment, idx) => {
            return (
              <MBox key={segment.id} mb={6}>
                <MFlex>
                  <MText fontWeight="semibold">Next Dates:</MText>
                  <MText ml={1}>
                    {toDateShort(segment.startDate)} -{' '}
                    {toDateShort(segment.endDate)}
                    {' ('}
                    {getLengthStartEndDisplay(
                      segment.startDate,
                      segment.endDate,
                    )}
                    {')'}
                  </MText>
                </MFlex>
                <PriceUpliftGrid
                  showUplift
                  namePrefix={`segments.${idx}`}
                  ppuLabel="Next PPU"
                  isLockedOrIsReadOnly={false}
                  items={segment.items}
                  control={control as any}
                  setValue={setValue as any}
                  percentageType={PriceUpliftPercentageTypeEnum.FIXED}
                  quote={quote}
                  quoteOffering={segment}
                  quotePrices={quotePrices}
                  offeringRate={offeringRate}
                  parentQuoteOfferingId={segment.parentQuoteOfferingId ?? ''}
                  calculateUpliftForAllItems={calculateUpliftForAllItems}
                  calculateUpliftForSingleItem={calculateUpliftForSingleItem}
                  clearProductUpliftsForSegment={clearProductUpliftsForSegment}
                />
                <MDivider my={2} />
              </MBox>
            );
          })}
          <MFlex justifyContent="space-between" mt={4}>
            <MButton
              type="button"
              variant="cancel"
              onClick={clearAll}
              disabled={!hasAnyUplifts || isLoading}
              _disabled={{
                cursor: 'not-allowed',
                color: 'tPurple.base',
                opacity: 0.5,
              }}
            >
              Clear
            </MButton>
            <MButton
              variant="primary"
              isLoading={isLoading}
              onClick={async () => {
                try {
                  await onApply?.();
                } catch (error) {
                  logger.error('Error saving uplifts:', error);
                }
              }}
              disabled={!hasAnyUplifts || isLoading}
            >
              Save
            </MButton>
          </MFlex>
        </MAccordionPanel>
      </MAccordionItem>
    </MAccordion>
  );
};
